import { httpClient } from "./client";
import type { StaffApi } from "./interfaces";
import type { UUID } from "../types";
import type {
  BookingCheckInStatusResponse,
  BookingParticipantResponse,
  CheckinRedemptionResponse,
} from "../types/generated";

/**
 * Staff / business-side check-in desk client (the operator half of court
 * reservations). All paths are under the /staff prefix — a status read or
 * check-in wired to /facilities/... would hit a non-existent route and 404
 * silently, so the prefix is load-bearing (and pinned by the tests).
 *
 * Authorization is server-side (facility:checkin RBAC); the client only carries
 * the request. A failed redemption (invalid/expired/used token) or a duplicate
 * mark rejects via the httpClient — never a fabricated success.
 */
export const staffApi: StaffApi = {
  // Per-participant check-in roster for one booking (who has arrived). 403 if
  // the staff member lacks facility:checkin.
  getBookingCheckInStatus: (facilityId: UUID, bookingId: UUID) =>
    httpClient.get<BookingCheckInStatusResponse>(
      `/staff/facilities/${encodeURIComponent(facilityId)}/bookings/${encodeURIComponent(bookingId)}/check-in-status`,
    ),

  // Front desk marks a roster participant present manually (check_in_method
  // becomes "staff"). 409 on a double-mark. The target id is a Firebase uid →
  // path-encoded.
  manualCheckInParticipant: (facilityId: UUID, bookingId: UUID, userId: string) =>
    httpClient.post<BookingParticipantResponse>(
      `/staff/facilities/${encodeURIComponent(facilityId)}/bookings/${encodeURIComponent(bookingId)}/participants/${encodeURIComponent(userId)}/check-in`,
    ),

  // Redeem a player's scanned QR pass token (single-use). 400 on an
  // invalid/expired/already-used token.
  redeemCheckin: (facilityId: UUID, data: { token: string }) =>
    httpClient.post<CheckinRedemptionResponse>(
      `/staff/facilities/${encodeURIComponent(facilityId)}/checkins/redeem`,
      data,
    ),
};
